import React, { useState, useRef, useEffect } from 'react';
import * as faceapi from '@vladmandic/face-api';
import axios from 'axios';
import { theme, cardStyle } from '../theme';

/**
 * FaceRegister — opens webcam, captures a clear face photo, extracts the
 * 128-dim descriptor client-side, and POSTs it to the server.
 *
 * Works for both new and existing patients. Shows live face detection
 * overlay so the user knows their face is visible before capturing.
 *
 * Props:
 *   API_BASE   — base API url
 *   patientId  — which patient to register
 *   patientName— display name
 *   onDone     — callback after registration completes
 *   onClose    — close the modal
 */
const FaceRegister = ({ API_BASE, patientId, patientName, onDone, onClose }) => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const animRef = useRef(null);

  const [modelsLoaded, setModelsLoaded] = useState(false);
  const [status, setStatus] = useState('Loading face models...');
  const [faceDetected, setFaceDetected] = useState(false);
  const [captured, setCaptured] = useState(null); // data URL
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);

  // Load models
  useEffect(() => {
    const load = async () => {
      try {
        await Promise.all([
          faceapi.nets.tinyFaceDetector.loadFromUri('/models'),
          faceapi.nets.faceLandmark68Net.loadFromUri('/models'),
          faceapi.nets.faceRecognitionNet.loadFromUri('/models'),
        ]);
        setModelsLoaded(true);
        setStatus('Starting camera...');
        startCamera();
      } catch (e) {
        setStatus('Failed to load face models');
      }
    };
    load();
    return () => {
      if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop());
      if (animRef.current) cancelAnimationFrame(animRef.current);
    };
  }, []); // eslint-disable-line

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { width: 480, height: 360, facingMode: 'user' }
      });
      streamRef.current = stream;
      if (videoRef.current) videoRef.current.srcObject = stream;
      setStatus('Position your face in the frame');
    } catch (e) {
      setStatus('Camera access denied');
    }
  };

  // Live face detection loop
  const detect = async () => {
    if (!videoRef.current || videoRef.current.readyState !== 4 || captured) return;
    const detection = await faceapi
      .detectSingleFace(videoRef.current, new faceapi.TinyFaceDetectorOptions({ scoreThreshold: 0.5 }));

    if (detection && canvasRef.current) {
      const dims = faceapi.matchDimensions(canvasRef.current, videoRef.current, true);
      const resized = faceapi.resizeResults(detection, dims);
      const ctx = canvasRef.current.getContext('2d');
      ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
      // Green box
      ctx.strokeStyle = '#00ff88';
      ctx.lineWidth = 3;
      ctx.strokeRect(resized.box.x, resized.box.y, resized.box.width, resized.box.height);
      setFaceDetected(true);
      setStatus('Face detected — click Capture');
    } else {
      setFaceDetected(false);
      setStatus('Position your face in the frame');
      if (canvasRef.current) {
        const ctx = canvasRef.current.getContext('2d');
        ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
      }
    }
    animRef.current = requestAnimationFrame(detect);
  };

  const handleVideoPlay = () => {
    if (!captured) animRef.current = requestAnimationFrame(detect);
  };

  const handleCapture = () => {
    if (!videoRef.current) return;
    const c = document.createElement('canvas');
    c.width = videoRef.current.videoWidth;
    c.height = videoRef.current.videoHeight;
    c.getContext('2d').drawImage(videoRef.current, 0, 0);
    const dataUrl = c.toDataURL('image/jpeg');
    setCaptured(dataUrl);
    setStatus('Processing face...');
    if (animRef.current) cancelAnimationFrame(animRef.current);
    // Extract and save
    extractAndSave(dataUrl);
  };

  const extractAndSave = async (dataUrl) => {
    setSaving(true);
    try {
      const img = await faceapi.fetchImage(dataUrl);
      const detection = await faceapi
        .detectSingleFace(img, new faceapi.TinyFaceDetectorOptions({ scoreThreshold: 0.3 }))
        .withFaceLandmarks()
        .withFaceDescriptor();

      if (!detection) {
        setStatus('No face detected in captured image. Try again.');
        setCaptured(null);
        setSaving(false);
        animRef.current = requestAnimationFrame(detect);
        return;
      }

      const descriptor = Array.from(detection.descriptor);
      await axios.post(`${API_BASE}/face/register`, { patientId, descriptor });
      setStatus('Face registered successfully!');
      setDone(true);
      // Stop camera
      if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop());
    } catch (e) {
      setStatus('Registration failed: ' + (e.response?.data?.error || e.message));
      setCaptured(null);
      animRef.current = requestAnimationFrame(detect);
    }
    setSaving(false);
  };

  const handleRetake = () => {
    setCaptured(null);
    setDone(false);
    setStatus('Position your face in the frame');
    startCamera().then(() => {
      animRef.current = requestAnimationFrame(detect);
    });
  };

  return (
    <div style={{
      position:'fixed', top:0, left:0, width:'100%', height:'100%',
      backgroundColor:'rgba(0,0,0,0.6)', zIndex:2000,
      display:'flex', alignItems:'center', justifyContent:'center',
    }} onClick={onClose}>
      <div onClick={e=>e.stopPropagation()}
        style={{
          backgroundColor:'white', borderRadius:'20px', padding:'30px',
          width:'520px', maxWidth:'95vw', boxShadow:'0 20px 60px rgba(0,0,0,0.3)',
        }}>
        <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'16px'}}>
          <div>
            <h2 style={{margin:0, color:theme.navy, fontSize:'1.2rem'}}>Register Face</h2>
            <p style={{margin:'4px 0 0', color:theme.textMuted, fontSize:'0.85rem'}}>{patientName}</p>
          </div>
          <button onClick={onClose} style={{background:'none', border:'none', fontSize:'1.5rem', cursor:'pointer', color:'#aaa'}}>✕</button>
        </div>

        {/* Status */}
        <div style={{
          padding:'8px 14px', borderRadius:'8px', marginBottom:'14px', textAlign:'center',
          fontSize:'0.88rem', fontWeight:'600',
          backgroundColor: done ? '#d1fae5' : faceDetected ? '#dbeafe' : '#fef3c7',
          color: done ? '#065f46' : faceDetected ? '#1e40af' : '#92400e',
        }}>
          {done ? '✓ ' : faceDetected ? '👤 ' : '⏳ '}{status}
        </div>

        {/* Camera / captured image */}
        <div style={{position:'relative', width:'100%', borderRadius:'12px', overflow:'hidden', backgroundColor:'#000', marginBottom:'16px'}}>
          {!captured ? (
            <>
              <video ref={videoRef} autoPlay muted playsInline
                style={{width:'100%', display:'block'}}
                onPlay={handleVideoPlay}/>
              <canvas ref={canvasRef}
                style={{position:'absolute', top:0, left:0, width:'100%', height:'100%'}}/>
            </>
          ) : (
            <img src={captured} alt="captured" style={{width:'100%', display:'block'}}/>
          )}

          {/* Scanning indicator */}
          {!captured && faceDetected && (
            <div style={{
              position:'absolute', top:'10px', right:'10px',
              padding:'4px 12px', backgroundColor:'rgba(0,255,136,0.8)',
              borderRadius:'12px', fontSize:'0.75rem', fontWeight:'700', color:'#000',
            }}>
              Face Detected
            </div>
          )}
        </div>

        {/* Buttons */}
        <div style={{display:'flex', gap:'10px', justifyContent:'center'}}>
          {!captured && !done && (
            <button onClick={handleCapture} disabled={!faceDetected || !modelsLoaded}
              style={{
                padding:'12px 28px',
                backgroundColor: faceDetected ? theme.success : '#ccc',
                color:'white', border:'none', borderRadius:'10px',
                cursor: faceDetected ? 'pointer' : 'not-allowed',
                fontWeight:'700', fontSize:'0.95rem',
              }}>
              {saving ? 'Processing...' : 'Capture & Register'}
            </button>
          )}
          {captured && !done && (
            <button onClick={handleRetake}
              style={{padding:'10px 22px', backgroundColor:'#6c757d', color:'white', border:'none', borderRadius:'8px', cursor:'pointer', fontWeight:'600'}}>
              Retake
            </button>
          )}
          {done && (
            <button onClick={()=>{ onDone && onDone(); onClose(); }}
              style={{padding:'12px 28px', backgroundColor:theme.success, color:'white', border:'none', borderRadius:'10px', cursor:'pointer', fontWeight:'700', fontSize:'0.95rem'}}>
              Done
            </button>
          )}
        </div>

        <style>{`@keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.3} }`}</style>
      </div>
    </div>
  );
};

export default FaceRegister;
